/*
 * lotto/index.js — the page: the host's draw, printable cards, checking a
 * claimed win by card number, and a player's own card on a phone.
 *
 * State lives in one object saved through __toolSettings (key `state`), so a
 * reload — or a phone that went to sleep in the middle of a party — brings
 * back the same game: which barrels are out and in what order, the card set
 * code, the settings. Each game type keeps its own draw, so switching from
 * lotto to bingo and back does not throw a game away.
 */
import * as E from './engine.js';
import { nickname, hasTradition } from './calls.js';
import { renderCard, completeLineNumbers } from './view.js';
import { ensureRtc, openHost, joinHost, stateMessage } from './sync.js';

const CFG = window.__LOTTO_CFG || { t: {}, locale: 'en' };
const SLUG = 'lotto';
const LOCALE = CFG.locale || document.documentElement.lang || 'en';
const MAX_PRINT = 900;
/* Languages whose bingo callers say the column letter («B 7»). In the others
 * the letter stays on screen and the voice reads the number. */
const SAY_LETTER = new Set(['en', 'es', 'pt', 'fr', 'de', 'id']);
const SPEECH_LANG = { ru: 'ru-RU', en: 'en-US', ko: 'ko-KR', ar: 'ar-SA', id: 'id-ID', pt: 'pt-BR', de: 'de-DE', es: 'es-MX', fr: 'fr-FR', uk: 'uk-UA' };
const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function T(key, params) {
    let s = CFG.t[key];
    if (s == null) s = key;
    // Longest key first: «:n» must not eat the start of «:nums» or «:nick».
    if (params) for (const k of Object.keys(params).sort((a, b) => b.length - a.length)) s = s.split(':' + k).join(String(params[k]));
    return s;
}
const $ = (id) => document.getElementById(id);
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
function stage(name, meta) { try { if (window.__stage) window.__stage(name, meta); } catch (_) {} }
function tele(action, data) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ action }, data || {})); } catch (_) {} }

/* ── state ───────────────────────────────────────────────────────────────── */

function load(k, fb) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, fb) : fb; } catch (_) { return fb; } }
function save(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }

const DEFAULT = {
    v: 1, game: LOCALE === 'ru' || LOCALE === 'uk' ? 'ru90' : (LOCALE === 'en' ? 'us75' : 'uk90'),
    code: null, draws: { ru90: [], us75: [], uk90: [] },
    goal: { ru90: 'full', us75: 'line', uk90: 'line1' },
    voice: true, nick: true, voiceURI: '', interval: 6, manual: false,
    perPage: { ru90: 4, us75: 4, uk90: 6 }, strips: true, qr: false,
    from: 1, to: 24, printed: {}, marks: {}, tab: 'host',
};
const S = Object.assign({}, DEFAULT, load('state', {}) || {});
for (const k of ['draws', 'goal', 'perPage', 'printed', 'marks']) S[k] = Object.assign({}, DEFAULT[k], S[k] || {});
if (!E.GAMES[S.game]) S.game = DEFAULT.game;
S.code = E.normalizeCode(S.code) || E.newCode();
for (const g of E.GAME_IDS) {
    // Defend against a hand-edited or truncated store: numbers in range, no repeats.
    const seen = new Set();
    S.draws[g] = (Array.isArray(S.draws[g]) ? S.draws[g] : []).filter((x) => Number.isInteger(x) && x >= 1 && x <= E.GAMES[g].max && !seen.has(x) && seen.add(x));
    if (!E.GAMES[g].goals.includes(S.goal[g])) S.goal[g] = DEFAULT.goal[g];
}
function persist() { save('state', S); }
persist();

const drawn = () => S.draws[S.game];
const G = () => E.GAMES[S.game];

/* ── voice ───────────────────────────────────────────────────────────────── */

let voices = [];
function refreshVoices() {
    if (!('speechSynthesis' in window)) { voices = []; return; }
    const all = window.speechSynthesis.getVoices() || [];
    const want = LOCALE.toLowerCase();
    const pref = (SPEECH_LANG[LOCALE] || '').toLowerCase();
    voices = all.filter((v) => String(v.lang || '').replace('_', '-').toLowerCase().split('-')[0] === want);
    // The locale's main region first, local voices before network ones.
    voices.sort((a, b) => {
        const ra = String(a.lang).replace('_', '-').toLowerCase() === pref ? 0 : 1;
        const rb = String(b.lang).replace('_', '-').toLowerCase() === pref ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return (a.localService === false) - (b.localService === false);
    });
    const pick = $('ltVoicePick');
    if (pick) {
        pick.innerHTML = '';
        voices.forEach((v) => {
            const o = document.createElement('option');
            o.value = v.voiceURI; o.textContent = v.name + ' (' + v.lang + ')';
            pick.appendChild(o);
        });
        if (voices.some((v) => v.voiceURI === S.voiceURI)) pick.value = S.voiceURI;
        $('ltVoiceRow').hidden = voices.length < 2;
    }
    renderVoiceNote();
}
let voicesSettled = false;
function renderVoiceNote() {
    const note = $('ltNoVoice');
    const box = $('ltVoice');
    const none = voicesSettled && voices.length === 0;
    if (note) note.hidden = !none;
    if (box) box.disabled = none;
}
function currentVoice() { return voices.find((v) => v.voiceURI === S.voiceURI) || voices[0] || null; }

function callText(n) {
    let base = String(n);
    if (S.game === 'us75' && SAY_LETTER.has(LOCALE)) base = E.letterOf75(n) + ' ' + n;
    const nick = S.nick ? nickname(LOCALE, n) : '';
    return nick ? T('call_fmt', { n: base, nick }) : base;
}
function speak(n) {
    if (!S.voice || !voices.length) return;
    const v = currentVoice();
    const text = callText(n);
    try {
        if (window.__speak) window.__speak(text, { lang: v ? v.lang : SPEECH_LANG[LOCALE], voice: v || undefined, rate: 0.95 });
    } catch (_) {}
}
function speechBusy() { try { return !!(window.speechSynthesis && window.speechSynthesis.speaking); } catch (_) { return false; } }

/* ── wake lock ───────────────────────────────────────────────────────────── */

/* The shared primitive (public/js/wake-lock.js) holds, verifies and repairs
 * the lock — a sentinel the OS dropped after a freeze is still a truthy
 * object, which is why a private copy is not allowed (WakeLockPrimitiveTest). */
let wantWake = false;
function setWake(on) {
    const W = window.__wakeLock;
    if (!W) return;
    try {
        if (on && (!wantWake || !W.active())) { const p = W.request(); if (p && p.catch) p.catch(() => {}); }
        else if (!on && wantWake) W.release();
    } catch (_) {}
    wantWake = on;
}
function syncWake() {
    const inGame = drawn().length > 0 && drawn().length < G().max;
    setWake(playerMode() || (S.tab === 'host' && (inGame || auto)));
}

/* ── the host: draw, undo, auto, board ───────────────────────────────────── */

let auto = false, autoTimer = 0;

function draw(manualNumber) {
    const d = drawn();
    let n = manualNumber;
    if (n == null) n = E.drawNext(S.game, d);
    if (n == null) { stopAuto(); toast(T('bag_empty', { max: G().max }), 'info'); return; }
    if (d.includes(n)) return;
    d.push(n);
    persist();
    if (d.length === 1) stage('draw_first', { game: S.game });
    renderHost(true);
    liveSend();
    speak(n);
    try { if (window.__announce) window.__announce(callText(n)); } catch (_) {}
    syncWake();
    if (d.length === G().max) {
        stopAuto();
        tele('lotto_bag_empty', { game: S.game });
    }
}

function undo() {
    const d = drawn();
    if (!d.length) return;
    stopAuto();
    const n = d.pop();
    persist();
    renderHost(false);
    liveSend();
    toast(T('undone', { n }), 'info');
    tele('lotto_undo', { game: S.game, left: d.length });
}

function startAuto() {
    if (auto) return;
    if (drawn().length >= G().max) return;
    auto = true;
    renderAutoBtn();
    tele('lotto_auto', { game: S.game, interval: S.interval });
    draw();
    scheduleAuto();
}
function scheduleAuto() {
    clearTimeout(autoTimer);
    if (!auto) return;
    autoTimer = setTimeout(function tick() {
        if (!auto) return;
        // Never talk over the previous call: wait for the voice to finish.
        if (speechBusy()) { autoTimer = setTimeout(tick, 250); return; }
        draw();
        scheduleAuto();
    }, Math.max(2, +S.interval || 6) * 1000);
}
function stopAuto() {
    if (!auto) return;
    auto = false;
    clearTimeout(autoTimer);
    renderAutoBtn();
    syncWake();
}
function renderAutoBtn() {
    const b = $('ltAuto');
    if (!b) return;
    b.setAttribute('aria-pressed', auto ? 'true' : 'false');
    b.textContent = auto ? '⏸ ' + T('auto_pause') : '▶ ' + T('auto_start');
}

function barrelLabel(n) { return S.game === 'us75' ? E.letterOf75(n) + ' ' + n : String(n); }

function renderHost(animate) {
    const d = drawn(), last = d[d.length - 1];
    const ball = $('ltBall');
    ball.dataset.kind = S.game === 'ru90' ? 'barrel' : 'ball';
    ball.dataset.letter = last && S.game === 'us75' ? E.letterOf75(last) : '';
    $('ltBallL').textContent = last && S.game === 'us75' ? E.letterOf75(last) : '';
    $('ltBallN').textContent = last ? String(last) : '?';
    ball.classList.toggle('is-empty', !last);
    ball.setAttribute('aria-label', last ? T('ball_aria', { n: barrelLabel(last) }) : T('none_yet'));
    if (animate && !REDUCED) {
        ball.classList.remove('is-roll');
        void ball.offsetWidth;
        ball.classList.add('is-roll');
    }
    const nick = last && S.nick ? nickname(LOCALE, last) : '';
    $('ltNick').textContent = nick;
    $('ltNick').hidden = !nick;
    $('ltCount').textContent = d.length ? T('count', { n: d.length, max: G().max }) : T('none_yet');
    const lastBox = $('ltLast');
    lastBox.innerHTML = '';
    d.slice(-6, -1).reverse().forEach((x) => {
        const s = document.createElement('span');
        s.className = 'lt-chip';
        s.textContent = barrelLabel(x);
        lastBox.appendChild(s);
    });
    $('ltLastWrap').hidden = d.length < 2;
    $('ltDraw').disabled = d.length >= G().max;
    $('ltDraw').textContent = d.length >= G().max ? T('bag_empty_short') : (S.game === 'ru90' ? T('draw_barrel') : T('draw_ball'));
    $('ltUndo').disabled = !d.length;
    renderBoard();
}

function renderBoard() {
    const board = $('ltBoard');
    const d = drawn();
    const pos = new Map(d.map((x, i) => [x, i]));
    const last = d[d.length - 1];
    board.innerHTML = '';
    board.dataset.game = S.game;
    board.classList.toggle('is-manual', !!S.manual);
    const tag = S.manual ? 'button' : 'span';
    const cell = (n) => {
        const c = document.createElement(tag);
        c.className = 'lt-bn no-tap-floor';
        c.textContent = String(n);
        c.dataset.n = String(n);
        if (tag === 'button') c.type = 'button';
        if (pos.has(n)) {
            c.classList.add('is-on');
            if (n === last) c.classList.add('is-last');
            c.title = T('board_cell_on', { at: pos.get(n) + 1 });
        }
        return c;
    };
    if (S.game === 'us75') {
        for (let k = 0; k < 5; k++) {
            const h = document.createElement('span');
            h.className = 'lt-bl lt-l' + k;
            h.textContent = E.BINGO[k];
            board.appendChild(h);
            for (let n = k * 15 + 1; n <= k * 15 + 15; n++) board.appendChild(cell(n));
        }
    } else {
        for (let n = 1; n <= 90; n++) board.appendChild(cell(n));
    }
}

/* ── new game / confirm ──────────────────────────────────────────────────── */

let confirmAction = null;
function askConfirm(text, yesLabel, action) {
    confirmAction = action;
    $('ltConfirmText').textContent = text;
    $('ltConfirmYes').textContent = yesLabel;
    $('ltConfirm').hidden = false;
    $('ltConfirmNo').focus({ preventScroll: true });
    $('ltConfirm').scrollIntoView({ block: 'nearest' });
}
function closeConfirm() { $('ltConfirm').hidden = true; confirmAction = null; }
function newGame() {
    const d = drawn();
    if (!d.length) return;
    stopAuto();
    askConfirm(T('new_confirm', { n: d.length }), T('new_yes'), () => {
        tele('lotto_new_game', { game: S.game, drawn: d.length });
        S.draws[S.game] = [];
        persist();
        renderHost(false);
        liveSend();
        renderCheck();
        syncWake();
    });
}

/* ── TV view / fullscreen ────────────────────────────────────────────────── */

function inFullscreen() { return (document.fullscreenElement || document.webkitFullscreenElement) === $('ltStage'); }
function setTv(on) {
    const st = $('ltStage');
    st.classList.toggle('lt-tv', on);
    $('ltTvExit').hidden = !on;
    $('ltFs').hidden = on;
    document.documentElement.classList.toggle('lt-tv-open', on && !inFullscreen());
}
function enterTv() {
    tele('lotto_tv', { game: S.game });
    const st = $('ltStage');
    const fallback = () => setTv(true);
    if (window.__safeFullscreen) {
        window.__safeFullscreen(st, { onUnsupported: fallback, onDenied: fallback }).then((ok) => { if (ok) setTv(true); });
    } else fallback();
}
function exitTv() {
    if (inFullscreen()) {
        try {
            const p = (document.exitFullscreen || document.webkitExitFullscreen).call(document);
            if (p && p.catch) p.catch(() => {});
        } catch (_) {}
    }
    setTv(false);
}
function onFsChange() { if (!inFullscreen() && $('ltStage').classList.contains('lt-tv') && !document.documentElement.classList.contains('lt-tv-open')) setTv(false); }
document.addEventListener('fullscreenchange', onFsChange);
document.addEventListener('webkitfullscreenchange', onFsChange);

/* ── tabs, game picker, code ─────────────────────────────────────────────── */

function setTab(tab) {
    if (!['host', 'cards', 'check'].includes(tab)) tab = 'host';
    S.tab = tab; persist();
    for (const t of ['host', 'cards', 'check']) {
        const b = $('ltTab-' + t), p = $('lt-' + t);
        b.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        b.tabIndex = t === tab ? 0 : -1;
        p.hidden = t !== tab;
    }
    if (tab !== 'host') stopAuto();
    if (tab === 'cards') renderCards();
    if (tab === 'check') renderCheck();
    syncWake();
}

function setGame(g) {
    if (!E.GAMES[g] || g === S.game) return;
    stopAuto();
    S.game = g; persist();
    tele('lotto_game', { game: g });
    renderAll();
    liveSend();
}

function renderGamePicker() {
    document.querySelectorAll('#ltGames .lt-game').forEach((b) => {
        const on = b.dataset.game === S.game;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
    });
}

function renderGoalSelects() {
    for (const id of ['ltGoal', 'ltGoal2']) {
        const sel = $(id);
        if (!sel) continue;
        sel.innerHTML = '';
        for (const goal of G().goals) {
            const o = document.createElement('option');
            o.value = goal; o.textContent = T('goal.' + goal);
            sel.appendChild(o);
        }
        sel.value = S.goal[S.game];
    }
}

function renderCode() {
    $('ltCode').textContent = S.code;
}
function applyCode(raw) {
    const c = E.normalizeCode(raw);
    if (!c) { toast(T('code_bad'), 'error'); return false; }
    if (c !== S.code) {
        S.code = c; persist();
        tele('lotto_code_typed', {});
        renderAll();
        liveSend();
    }
    return true;
}

/* ── cards (print) ───────────────────────────────────────────────────────── */

function readRange() {
    let from = parseInt($('ltFrom').value, 10), to = parseInt($('ltTo').value, 10);
    if (!(from >= 1)) from = 1;
    if (!(to >= from)) to = from;
    if (to - from + 1 > MAX_PRINT) to = from + MAX_PRINT - 1;
    if (to > E.MAX_CARD) to = E.MAX_CARD;
    return { from, to };
}

function cardTitle(n) { return T('card_title', { game: T('game.' + S.game), n }); }
/* The code isolated left-to-right: a code that starts with a digit after an
 * Arabic word otherwise has its digit carried to the other end («7XPK9»
 * showed as «XPK9 7»). The PDF gets the plain string — its own bidi split
 * (PdfTextFont) handles the runs, and fontkit ignores isolate marks. */
function codeLine(code) { return T('code_short', { code: String.fromCharCode(0x2066) + code + String.fromCharCode(0x2069) }); }

function renderCards() {
    $('ltFrom').value = S.from; $('ltTo').value = S.to;
    document.querySelectorAll('#ltPerPage input').forEach((r) => { r.checked = +r.value === S.perPage[S.game]; });
    const isUk = S.game === 'uk90';
    $('ltStripsRow').hidden = !isUk;
    $('ltStrips').checked = !!S.strips;
    $('ltPerPage').hidden = isUk && S.strips;
    $('ltQr').checked = !!S.qr;
    renderCardsSummary();
    renderPreview();
    renderPhoneLink();
}
function renderCardsSummary() {
    const r = readRange();
    const job = { game: S.game, from: r.from, to: r.to, strips: S.strips };
    const perPage = S.game === 'uk90' && S.strips ? 6 : S.perPage[S.game];
    const count = (S.game === 'uk90' && S.strips) ? (Math.ceil(r.to / 6) - Math.ceil(r.from / 6) + 1) * 6 : r.to - r.from + 1;
    const pages = Math.ceil(count / perPage);
    $('ltCardsSum').textContent = T('cards_sum', { cards: count, pages });
    return job;
}
function renderPreview() {
    const box = $('ltPreview');
    box.innerHTML = '';
    const n = readRange().from;
    const c = E.card(S.game, S.code, n);
    box.appendChild(renderCard(c, { title: cardTitle(n), sub: codeLine(S.code), freeLabel: T('free') }));
    $('ltPreviewH').textContent = T('preview_title', { n });
}

function playerUrl(n, opts) {
    const base = location.origin + '/' + LOCALE + '/' + SLUG;
    // An open room travels in the link: the phone joins it on open. A printed
    // card never carries one — rooms live for one evening, paper for years.
    const room = (opts && opts.room && live && S.room) ? '&room=' + S.room : '';
    return base + '#' + E.cardHash(S.game, S.code, n) + room;
}

function loadScripts(list, label, probe) {
    if (probe()) return Promise.resolve();
    if (window.__assetChain && window.__assetChain.loadScript) return window.__assetChain.loadScript(list, label);
    return new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = list[0]; s.async = true;
        s.onload = () => res(); s.onerror = () => rej(new Error(label + ' failed to load'));
        document.head.appendChild(s);
    });
}
function ensureLibs(needQr) {
    const a = CFG.assets || {};
    const jobs = [loadScripts(a.pdfLib || ['/vendor/pdf-lib/pdf-lib.min.js'], 'pdf-lib', () => window.PDFLib),
                  loadScripts(a.pdfFont || ['/js/pdf-text-font.js'], 'pdf-text-font', () => window.PdfTextFont)];
    if (needQr) jobs.push(ensureQr());
    return Promise.all(jobs);
}
function ensureQr() { return loadScripts((CFG.assets || {}).qr || ['/vendor/qrcode/qrcode.min.js'], 'qrcode', () => window.qrcode); }

let pdfBusy = false;
async function downloadPdf() {
    if (pdfBusy) return;
    const r = readRange();
    S.from = r.from; S.to = r.to; persist();
    const btn = $('ltPdf'), status = $('ltPdfStatus');
    pdfBusy = true; btn.disabled = true;
    status.hidden = false;
    status.textContent = T('pdf_busy', { p: 0 });
    stage('pdf_start', { game: S.game });
    const t0 = performance.now();
    try {
        await ensureLibs(S.qr);
        const mod = await import('./pdf.js');
        const job = {
            game: S.game, code: S.code, from: r.from, to: r.to,
            perPage: S.perPage[S.game], strips: S.game === 'uk90' && S.strips, qr: S.qr,
            qrUrl: (n) => playerUrl(n),
            docTitle: T('game.' + S.game) + ' · ' + S.code,
            text: {
                title: (n) => cardTitle(n),
                sub: T('code_short', { code: S.code }),
                footer: T('pdf_footer', { site: location.host + '/' + LOCALE + '/' + SLUG, code: S.code }),
            },
            onProgress: (p) => { status.textContent = T('pdf_busy', { p: Math.round(p * 100) }); },
        };
        const res = await mod.build(job);
        const rr = mod.range(job);
        const name = 'lotto-' + S.game + '-' + S.code + '-' + rr.from + '-' + rr.to + '.pdf';
        if (window.__downloadFile) window.__downloadFile(res.blob, name);
        status.textContent = T('pdf_done', { cards: res.cards, pages: res.pages });
        S.printed[S.game + '.' + S.code] = { from: rr.from, to: rr.to };
        persist();
        try { window.__aeToolComplete && window.__aeToolComplete({ action: 'pdf', game: S.game, cards: res.cards, pages: res.pages, per_page: job.strips ? 6 : job.perPage, qr: S.qr ? 1 : 0, ms: Math.round(performance.now() - t0) }); } catch (_) {}
    } catch (e) {
        status.textContent = T('pdf_fail');
        toast(T('pdf_fail'), 'error');
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'lotto_pdf_failed', msg: String((e && e.message) || e).slice(0, 160) }); } catch (_) {}
        try { console.error(e); } catch (_) {}
    } finally {
        pdfBusy = false; btn.disabled = false;
    }
}

async function renderPhoneLink() {
    let n = parseInt($('ltPhoneNum').value, 10);
    if (!(n >= 1 && n <= E.MAX_CARD)) n = readRange().from;
    const url = playerUrl(n, { room: true });
    const a = $('ltPhoneLink');
    a.href = url; a.textContent = url.replace(/^https?:\/\//, '');
    const box = $('ltPhoneQr');
    try {
        await ensureQr();
        const q = window.qrcode(0, 'M');
        q.addData(url); q.make();
        const m = q.getModuleCount(), cell = 4, pad = 2;
        const cv = document.createElement('canvas');
        cv.width = cv.height = (m + pad * 2) * cell;
        const ctx = cv.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.fillStyle = '#000';
        for (let r = 0; r < m; r++) for (let c = 0; c < m; c++) if (q.isDark(r, c)) ctx.fillRect((c + pad) * cell, (r + pad) * cell, cell, cell);
        cv.setAttribute('role', 'img');
        cv.setAttribute('aria-label', T('phone_qr_aria', { n }));
        box.innerHTML = '';
        box.appendChild(cv);
    } catch (_) { box.innerHTML = ''; }
}

/* ── check a card ────────────────────────────────────────────────────────── */

function verdictHtml(c, goal) {
    const d = drawn();
    const v = E.evaluate(c, d, goal);
    const wrap = document.createElement('div');
    wrap.className = 'lt-verdict ' + (v.met ? 'is-win' : 'is-no');
    const h = document.createElement('div');
    h.className = 'lt-v-h';
    h.textContent = (v.met ? '✅ ' : '⏳ ') + (v.met ? T('v_win', { goal: T('goal.' + goal) }) : T('v_no', { goal: T('goal.' + goal) }));
    wrap.appendChild(h);
    const p = document.createElement('p');
    if (!d.length) p.textContent = T('v_none_drawn');
    else if (v.met) {
        const ago = d.length - v.at;
        p.textContent = ago === 0 ? T('v_now', { num: barrelLabel(v.number) }) : T('v_ago', { at: v.at, num: barrelLabel(v.number), k: ago });
    } else {
        const nums = v.missing.map(barrelLabel).join(', ');
        p.textContent = v.missing.length === 1 ? T('v_need1', { nums }) : T('v_need', { k: v.missing.length, nums });
    }
    wrap.appendChild(p);
    const extra = document.createElement('p');
    extra.className = 'lt-v-x';
    const hit = c.numbers.filter((x) => d.includes(x)).length;
    extra.textContent = T(c.game === 'us75' ? 'v_lines75' : 'v_lines', { k: v.linesDone }) + ' · ' + T('v_hit', { k: hit, total: c.numbers.length });
    wrap.appendChild(extra);
    return { el: wrap, v };
}

function checkCard(nRaw) {
    const out = $('ltCheckOut');
    const n = parseInt(nRaw, 10);
    out.innerHTML = '';
    if (!(n >= 1 && n <= E.MAX_CARD)) { if (String(nRaw).trim()) toast(T('check_bad', { max: E.MAX_CARD }), 'error'); return; }
    const c = E.card(S.game, S.code, n);
    const goal = S.goal[S.game];
    const d = drawn();
    const have = new Set(d);
    const { el, v } = verdictHtml(c, goal);
    out.appendChild(el);
    out.appendChild(renderCard(c, {
        title: cardTitle(n), sub: codeLine(S.code), drawn: have, last: d[d.length - 1],
        highlight: completeLineNumbers(c, have), freeLabel: T('free'),
    }));
    lastChecked = n;
    stage('check_card', { game: S.game, met: v.met ? 1 : 0 });
    tele('lotto_check', { game: S.game, goal, met: v.met ? 1 : 0, drawn: d.length });
    if (v.met) {
        try { window.__aeToolComplete && window.__aeToolComplete({ action: 'verified_win', game: S.game, goal, at: v.at, drawn: d.length }); } catch (_) {}
    }
    el.scrollIntoView({ block: 'nearest' });
}
let lastChecked = null;

function scan() {
    const from = Math.max(1, parseInt($('ltScanFrom').value, 10) || 1);
    let to = Math.max(from, parseInt($('ltScanTo').value, 10) || from);
    if (to - from > 4999) to = from + 4999;
    const goal = S.goal[S.game];
    const list = E.winners(S.game, S.code, from, to, drawn(), goal);
    const out = $('ltScanOut');
    out.innerHTML = '';
    const p = document.createElement('p');
    p.textContent = list.length ? T('scan_found', { goal: T('goal.' + goal), k: list.length }) : T('scan_none', { from, to, goal: T('goal.' + goal) });
    out.appendChild(p);
    if (list.length) {
        const ul = document.createElement('div');
        ul.className = 'lt-scan-list';
        list.slice(0, 200).forEach((w) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'lt-chip lt-chip-btn';
            b.textContent = T('scan_item', { n: w.n, at: w.at });
            b.addEventListener('click', () => { $('ltCheckNum').value = w.n; checkCard(w.n); });
            ul.appendChild(b);
        });
        out.appendChild(ul);
    }
    tele('lotto_scan', { game: S.game, goal, cards: to - from + 1, found: list.length });
}

function renderCheck() {
    const pr = S.printed[S.game + '.' + S.code];
    if (!$('ltScanFrom').value) $('ltScanFrom').value = pr ? pr.from : 1;
    if (!$('ltScanTo').value || $('ltScanTo').dataset.auto === '1') { $('ltScanTo').value = pr ? pr.to : Math.max(S.to, 24); $('ltScanTo').dataset.auto = '1'; }
    $('ltCheckCount').textContent = drawn().length ? T('count', { n: drawn().length, max: G().max }) : T('v_none_drawn');
    if (lastChecked && !$('lt-check').hidden) checkCard(lastChecked);
}

/* ── the player's phone ──────────────────────────────────────────────────── */

let player = null;
function playerMode() { return !!player; }
function roomFromHash(h) { const m = /(?:^|[#&])room=([a-z0-9]{6,8})(?:&|$)/.exec(String(h || '')); return m ? m[1] : ''; }

function openPlayer(p) {
    if (player && player.link) player.link.close();
    const key = p.game + '.' + p.code + '.' + p.n;
    player = Object.assign({}, p, {
        key, c: E.card(p.game, p.code, p.n),
        marks: new Set(Array.isArray(S.marks[key]) ? S.marks[key] : []),
        called: null, last: null, count: 0, link: null, room: roomFromHash(location.hash) || '',
    });
    $('ltHostUi').hidden = true;
    $('ltPlayer').hidden = false;
    document.documentElement.classList.add('lt-has-player');
    stopAuto();
    $('ltPlTitle').textContent = T('card_title', { game: T('game.' + p.game), n: p.n });
    $('ltPlSub').textContent = codeLine(p.code);
    $('ltPlNum').value = p.n;
    $('ltPlRoom').value = player.room || S.plRoom || '';
    $('ltPlAuto').checked = S.plAuto !== false;
    const jump = $('ltJumpBtn');
    if (jump) jump.textContent = T('pl_jump', { n: p.n });
    $('ltPlLast').hidden = true;
    $('ltPlLiveStatus').textContent = '';
    drawPlayer();
    stage('player_card', { game: p.game });
    tele('lotto_player_open', { game: p.game, room: player.room ? 1 : 0 });
    syncWake();
    if (player.room) playerJoin(player.room);
}

function savePlayerMarks() {
    S.marks[player.key] = [...player.marks];
    // keep the store small: the 20 most recent cards
    const keys = Object.keys(S.marks);
    if (keys.length > 20) delete S.marks[keys[0]];
    persist();
}

function drawPlayer(focusN) {
    const pl = player, c = pl.c, marks = pl.marks;
    const box = $('ltPlCard');
    box.innerHTML = '';
    const near = new Set(E.oneToGo(c, marks).map((x) => x.number));
    // Called by the host but not daubed yet (auto-marking off): a ring, not a mark.
    const hint = new Set();
    if (pl.called) for (const x of c.numbers) if (pl.called.has(x) && !marks.has(x)) hint.add(x);
    box.appendChild(renderCard(c, {
        marked: marks, near, called: hint, last: pl.last, highlight: completeLineNumbers(c, marks), interactive: true, freeLabel: T('free'),
        onToggle: (n) => {
            if (marks.has(n)) marks.delete(n); else marks.add(n);
            savePlayerMarks();
            drawPlayer(n);
        },
    }));
    if (focusN != null) {
        const b = box.querySelector('[data-n="' + focusN + '"]');
        if (b) b.focus({ preventScroll: true });
    }
    renderPlayerMsg(c, marks);
}

function renderPlayerMsg(c, marks) {
    const msg = $('ltPlMsg');
    const full = c.numbers.every((x) => marks.has(x));
    const done = E.lines(c).filter((l) => l.nums.every((x) => marks.has(x))).length;
    const near = E.oneToGo(c, marks);
    let text = '', cls = '';
    if (full) { text = T('pl_full'); cls = 'is-win'; }
    else if (c.game === 'us75') {
        const corners = [c.grid[0][0], c.grid[0][4], c.grid[4][0], c.grid[4][4]].every((x) => marks.has(x));
        if (done) { text = T('pl_line75'); cls = 'is-win'; }
        else if (corners) { text = T('pl_corners'); cls = 'is-win'; }
        else if (near.length) { text = T('pl_near75'); cls = 'is-near'; }
    } else if (done) { text = T(done === 1 ? 'pl_row1' : 'pl_row2'); cls = 'is-win'; }
    else if (near.length) { text = T('pl_near'); cls = 'is-near'; }
    msg.textContent = text;
    msg.className = 'lt-pl-msg ' + cls;
    msg.hidden = !text;
}

/* The host's draw arrives: the whole list every time, so a phone that slept
 * or joined late is right after one message. */
function onHostState(st) {
    const pl = player;
    if (!pl) return;
    if (st.game !== pl.game || st.set !== pl.code) {
        pl.called = null;
        $('ltPlLiveStatus').textContent = T('pl_live_other', { game: T('game.' + st.game), code: st.set });
        $('ltPlLast').hidden = true;
        drawPlayer();
        return;
    }
    const was = pl.count;
    pl.called = new Set(st.drawn);
    pl.last = st.drawn[st.drawn.length - 1] || null;
    pl.count = st.drawn.length;
    if ($('ltPlAuto').checked) {
        // Auto-marking follows the host exactly — an Undo there un-marks here.
        pl.marks = new Set(pl.c.numbers.filter((x) => pl.called.has(x)));
        savePlayerMarks();
    }
    const last = $('ltPlLast');
    last.hidden = !pl.last;
    if (pl.last) last.textContent = T('pl_last', { num: barrelLabelFor(pl.game, pl.last), k: pl.count });
    $('ltPlLiveStatus').textContent = T('pl_live_ok');
    drawPlayer();
    if (pl.count > was && pl.c.numbers.includes(pl.last)) {
        try { navigator.vibrate && navigator.vibrate(60); } catch (_) {}
    }
}
function barrelLabelFor(game, n) { return game === 'us75' ? E.letterOf75(n) + ' ' + n : String(n); }

function playerJoin(raw) {
    const pl = player;
    if (!pl) return;
    if (pl.link) { pl.link.close(); pl.link = null; }
    const status = $('ltPlLiveStatus');
    status.textContent = T('pl_live_connecting');
    ensureRtc(CFG.assets || {}, loadScripts).then(() => {
        if (player !== pl) return;
        pl.link = joinHost({
            code: raw, games: E.GAMES, onState: onHostState,
            onStatus: (kind, code) => {
                if (player !== pl) return;
                const map = { connecting: 'pl_live_connecting', connected: 'pl_live_ok', not_found: 'pl_live_not_found',
                    closed: 'pl_live_closed', error: 'pl_live_error', bad_code: 'pl_live_bad' };
                status.textContent = T(map[kind] || 'pl_live_error');
                if (kind === 'connected') {
                    pl.room = code; S.plRoom = code; persist();
                    tele('lotto_live_join', { ok: 1 });
                } else if (kind !== 'connecting') {
                    tele('lotto_live_join', { ok: 0, why: kind });
                }
            },
        });
    }, () => { status.textContent = T('pl_live_error'); tele('lotto_live_join', { ok: 0, why: 'scripts' }); });
}

function closePlayer() {
    if (player && player.link) player.link.close();
    player = null;
    $('ltPlayer').hidden = true;
    $('ltHostUi').hidden = false;
    document.documentElement.classList.remove('lt-has-player');
    try { history.replaceState(null, '', location.pathname + location.search); } catch (_) {}
    renderAll();
}
function readHash() {
    const p = E.parseCardHash(location.hash);
    if (p) {
        // The same card with a new room code is a re-join, not a new card.
        if (player && player.key === p.game + '.' + p.code + '.' + p.n) {
            const r = roomFromHash(location.hash);
            if (r && r !== player.room) { $('ltPlRoom').value = r; playerJoin(r); }
            return;
        }
        openPlayer(p);
    } else if (player) closePlayer();
}

/* ── the host's room ─────────────────────────────────────────────────────── */

let live = null;
const ROOM_TTL = 12 * 3600 * 1000;
function liveOpen(auto) {
    if (live) return;
    const status = $('ltLiveStatus');
    status.textContent = T('live_opening');
    $('ltLiveOn').disabled = true;
    ensureRtc(CFG.assets || {}, loadScripts).then(() => {
        const wanted = S.room;
        live = openHost({
            code: wanted || null,
            current: () => stateMessage(S.game, S.code, drawn()),
            onOpen: (code) => {
                if (wanted && wanted !== code) toast(T('live_new_code', { code }), 'info');
                S.room = code; S.roomAt = Date.now(); persist();
                status.textContent = '';
                renderLive();
                if (!$('lt-cards').hidden) renderPhoneLink();
                tele('lotto_live_open', { auto: auto ? 1 : 0 });
            },
            onCount: (n) => { $('ltLiveCount').textContent = T('live_count', { n }); },
            onError: (type) => {
                if (type === 'peer-unavailable') return;          // a phone that left
                status.textContent = T('live_err');
                tele('lotto_live_error', { type });
            },
        });
        renderLive();
    }, () => { status.textContent = T('live_err'); $('ltLiveOn').disabled = false; tele('lotto_live_error', { type: 'scripts' }); });
}
function liveClose() {
    if (live) live.close();
    live = null;
    S.room = null; persist();
    renderLive();
    if (!$('lt-cards').hidden) renderPhoneLink();
}
function liveSend() { if (live) live.send(); }
function renderLive() {
    const open = !!(live && S.room);
    $('ltLiveOn').hidden = open;
    $('ltLiveOn').disabled = !!live && !open;
    $('ltLiveOpen').hidden = !open;
    $('ltRoomCode').textContent = open ? S.room : '';
    $('ltTvRoom').hidden = !open;
    $('ltTvRoom').textContent = open ? T('tv_room', { code: S.room }) : '';
    if (open && !$('ltLiveCount').textContent) $('ltLiveCount').textContent = T('live_count', { n: 0 });
}

/* ── wiring ──────────────────────────────────────────────────────────────── */

function renderAll() {
    renderGamePicker();
    renderGoalSelects();
    renderCode();
    $('ltVoice').checked = !!S.voice;
    $('ltNickOn').checked = !!S.nick;
    $('ltNickRow').hidden = !hasTradition(LOCALE);
    $('ltManual').checked = !!S.manual;
    $('ltManualHint').hidden = !S.manual;
    $('ltInterval').value = String(S.interval);
    renderAutoBtn();
    renderHost(false);
    setTab(S.tab);
}

function isTyping(t) {
    return t && (t.isContentEditable || /^(INPUT|SELECT|TEXTAREA|BUTTON|A|SUMMARY)$/.test(t.tagName));
}

function wire() {
    $('ltGames').addEventListener('click', (e) => {
        const b = e.target.closest('.lt-game');
        if (b) setGame(b.dataset.game);
    });
    $('ltGames').addEventListener('keydown', (e) => {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
        e.preventDefault();
        const order = [...document.querySelectorAll('#ltGames .lt-game')].map((b) => b.dataset.game);
        let i = order.indexOf(S.game) + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1);
        if (document.documentElement.dir === 'rtl' && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) i = order.indexOf(S.game) + (e.key === 'ArrowLeft' ? 1 : -1);
        i = (i + order.length) % order.length;
        setGame(order[i]);
        const nb = document.querySelector('#ltGames [data-game="' + order[i] + '"]');
        if (nb) nb.focus();
    });
    document.querySelectorAll('.lt-tab').forEach((b) => b.addEventListener('click', () => setTab(b.dataset.tab)));
    $('ltTabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const order = ['host', 'cards', 'check'];
        const fwd = (e.key === 'ArrowRight') !== (document.documentElement.dir === 'rtl');
        const i = (order.indexOf(S.tab) + (fwd ? 1 : 2)) % 3;
        setTab(order[i]);
        $('ltTab-' + order[i]).focus();
    });

    $('ltDraw').addEventListener('click', () => { stopAuto(); draw(); });
    $('ltUndo').addEventListener('click', undo);
    $('ltAuto').addEventListener('click', () => (auto ? stopAuto() : startAuto()));
    $('ltInterval').addEventListener('change', (e) => { S.interval = +e.target.value || 6; persist(); if (auto) scheduleAuto(); });
    $('ltFs').addEventListener('click', enterTv);
    $('ltTvExit').addEventListener('click', exitTv);
    $('ltNew').addEventListener('click', newGame);
    $('ltConfirmYes').addEventListener('click', () => { const a = confirmAction; closeConfirm(); if (a) a(); });
    $('ltConfirmNo').addEventListener('click', closeConfirm);
    $('ltBoard').addEventListener('click', (e) => {
        if (!S.manual) return;
        const b = e.target.closest('.lt-bn');
        if (!b) return;
        const n = +b.dataset.n;
        const at = drawn().indexOf(n);
        if (at >= 0) { toast(T('manual_dup', { n: barrelLabel(n), at: at + 1 }), 'info'); return; }
        draw(n);
    });

    $('ltVoice').addEventListener('change', (e) => {
        S.voice = e.target.checked; persist();
        if (!S.voice) { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (_) {} }
        tele('lotto_voice', { on: S.voice ? 1 : 0, voices: voices.length });
    });
    $('ltVoicePick').addEventListener('change', (e) => { S.voiceURI = e.target.value; persist(); const d = drawn(); if (d.length) speak(d[d.length - 1]); });
    $('ltNickOn').addEventListener('change', (e) => { S.nick = e.target.checked; persist(); renderHost(false); });
    $('ltManual').addEventListener('change', (e) => {
        S.manual = e.target.checked; persist();
        if (S.manual) stopAuto();
        $('ltManualHint').hidden = !S.manual;
        renderBoard();
        tele('lotto_manual', { on: S.manual ? 1 : 0 });
    });
    for (const id of ['ltGoal', 'ltGoal2']) {
        $(id).addEventListener('change', (e) => {
            S.goal[S.game] = e.target.value; persist();
            renderGoalSelects();
            if (!$('lt-check').hidden) renderCheck();
        });
    }

    // code
    $('ltCodeEdit').addEventListener('click', () => {
        const f = $('ltCodeForm');
        f.hidden = !f.hidden;
        $('ltCodeEdit').setAttribute('aria-expanded', f.hidden ? 'false' : 'true');
        if (!f.hidden) { $('ltCodeIn').value = S.code; $('ltCodeIn').focus({ preventScroll: true }); }
    });
    $('ltCodeApply').addEventListener('click', () => { if (applyCode($('ltCodeIn').value)) { $('ltCodeForm').hidden = true; $('ltCodeEdit').setAttribute('aria-expanded', 'false'); } });
    $('ltCodeIn').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); $('ltCodeApply').click(); } });
    $('ltCodeNew').addEventListener('click', () => {
        const old = S.code;
        S.code = E.newCode(); persist();
        $('ltCodeForm').hidden = true; $('ltCodeEdit').setAttribute('aria-expanded', 'false');
        toast(T('code_new_done', { old, code: S.code }), 'info');
        tele('lotto_code_new', {});
        renderAll();
        liveSend();
    });

    // cards
    const onRange = () => { const r = readRange(); S.from = r.from; S.to = r.to; persist(); renderCardsSummary(); renderPreview(); };
    $('ltFrom').addEventListener('change', onRange);
    $('ltTo').addEventListener('change', onRange);
    $('ltFrom').addEventListener('input', () => renderCardsSummary());
    $('ltTo').addEventListener('input', () => renderCardsSummary());
    $('ltPerPage').addEventListener('change', (e) => { if (e.target.name === 'ltPer') { S.perPage[S.game] = +e.target.value; persist(); renderCardsSummary(); } });
    $('ltStrips').addEventListener('change', (e) => { S.strips = e.target.checked; persist(); renderCards(); });
    $('ltQr').addEventListener('change', (e) => { S.qr = e.target.checked; persist(); });
    $('ltPdf').addEventListener('click', downloadPdf);
    $('ltPhoneNum').addEventListener('change', renderPhoneLink);
    $('ltPhoneOpen').addEventListener('click', () => { location.hash = new URL($('ltPhoneLink').href).hash; });
    $('ltPhoneCopy').addEventListener('click', () => {
        const url = $('ltPhoneLink').href;
        const done = () => toast(T('copied'), 'success');
        if (window.__copyText) window.__copyText(url).then((ok) => { if (ok !== false) done(); });
        else if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, () => {});
    });

    // check
    $('ltCheckBtn').addEventListener('click', () => checkCard($('ltCheckNum').value));
    $('ltCheckNum').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); checkCard($('ltCheckNum').value); } });
    $('ltScanTo').addEventListener('input', () => { $('ltScanTo').dataset.auto = '0'; });
    $('ltScanBtn').addEventListener('click', scan);

    // player
    $('ltPlGo').addEventListener('click', () => {
        const n = parseInt($('ltPlNum').value, 10);
        if (!(n >= 1 && n <= E.MAX_CARD) || !player) return;
        location.hash = E.cardHash(player.game, player.code, n) + (player.room ? '&room=' + player.room : '');
    });
    $('ltPlHost').addEventListener('click', closePlayer);
    $('ltPlReset').addEventListener('click', () => { if (!player) return; player.marks.clear(); savePlayerMarks(); drawPlayer(); });
    $('ltPlJoin').addEventListener('click', () => playerJoin($('ltPlRoom').value));
    $('ltPlRoom').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); playerJoin($('ltPlRoom').value); } });
    $('ltPlAuto').addEventListener('change', (e) => {
        S.plAuto = e.target.checked; persist();
        if (player && player.called && e.target.checked) {
            player.marks = new Set(player.c.numbers.filter((x) => player.called.has(x)));
            savePlayerMarks();
        }
        if (player) drawPlayer();
    });

    // the host's room
    $('ltLiveOn').addEventListener('click', () => liveOpen(false));
    $('ltLiveOff').addEventListener('click', liveClose);
    const jump = $('ltJumpBtn');
    if (jump) jump.addEventListener('click', () => $('ltPlayer').scrollIntoView({ block: 'nearest' }));
    window.addEventListener('hashchange', readHash);

    // keyboard
    document.addEventListener('keydown', (e) => {
        if (e.ctrlKey || e.metaKey || e.altKey || playerMode()) return;
        if (e.key === 'Escape' && $('ltStage').classList.contains('lt-tv')) { exitTv(); return; }
        if (S.tab !== 'host' || !$('ltConfirm').hidden) return;
        if (isTyping(e.target)) return;
        const k = e.key;
        if (k === ' ' || k === 'Enter' || k === 'Spacebar') { e.preventDefault(); if (!e.repeat) { stopAuto(); draw(); } }
        else if (k === 'a' || k === 'A' || k === 'ф' || k === 'Ф') { auto ? stopAuto() : startAuto(); }
        else if (k === 'f' || k === 'F' || k === 'а' || k === 'А') { $('ltStage').classList.contains('lt-tv') ? exitTv() : enterTv(); }
    });

    if ('speechSynthesis' in window) {
        refreshVoices();
        try { window.speechSynthesis.addEventListener('voiceschanged', refreshVoices); } catch (_) {}
        setTimeout(() => { voicesSettled = true; refreshVoices(); }, 1800);
    } else { voicesSettled = true; renderVoiceNote(); }
}

wire();
renderAll();
readHash();
// A room opened this evening comes back after a reload with the same code,
// so phones that were in it reconnect by themselves.
if (!playerMode() && S.room && S.roomAt && Date.now() - S.roomAt < ROOM_TTL) liveOpen(true);
else if (S.room) { S.room = null; persist(); }
document.documentElement.classList.add('lt-ready');
